/** 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Core owns row-action paint protection and layout scroll; official sync distributes it.
 */
export type PaintRect = { left: number; right: number; top: number; bottom: number };

export function paintRectsOverlap(a: PaintRect, b: PaintRect): boolean {
    return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

function clip(rect: PaintRect, bounds: PaintRect): PaintRect | null {
    const value = { left: Math.max(rect.left, bounds.left), right: Math.min(rect.right, bounds.right),
        top: Math.max(rect.top, bounds.top), bottom: Math.min(rect.bottom, bounds.bottom) };
    return value.right > value.left && value.bottom > value.top ? value : null;
}

/** Paint geometry only: record text is never logged, retained or sent anywhere. */
export function getTableContentPaintRects(viewport: Element): PaintRect[] {
    const bounds = viewport.getBoundingClientRect();
    const rects: PaintRect[] = [];
    for (const row of viewport.querySelectorAll('tbody > tr:not([data-row-actions-space])')) {
        if (!paintRectsOverlap(row.getBoundingClientRect(), bounds)) continue;
        for (const cell of row.querySelectorAll(':scope > td')) {
            const cellBounds = cell.getBoundingClientRect();
            const rendered = (element: Element) => {
                const style = getComputedStyle(element);
                return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
            };
            for (const control of cell.querySelectorAll('button,a[href],input,select,textarea,[role="button"],[role="checkbox"],[contenteditable="true"],img,svg,canvas,[role="img"]')) {
                if (!rendered(control)) continue;
                const value = clip(control.getBoundingClientRect(), bounds);
                if (value) rects.push(value);
            }
            const walker = cell.ownerDocument.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
            while (walker.nextNode()) {
                const text = walker.currentNode;
                const parent = text.parentElement;
                // ARIA describes semantics, not painted visibility; decorative text can still be visible.
                if (!text.nodeValue?.trim() || !parent || !rendered(parent)) continue;
                const range = cell.ownerDocument.createRange(); range.selectNode(text);
                for (const raw of range.getClientRects()) {
                    let value = clip(raw, bounds);
                    if (value) value = clip(value, cellBounds);
                    for (let ancestor: Element | null = parent; value && ancestor && ancestor !== cell; ancestor = ancestor.parentElement) {
                        const style = getComputedStyle(ancestor), box = ancestor.getBoundingClientRect();
                        if (style.display === 'none' || style.visibility === 'hidden' || style.clip !== 'auto') { value = null; break; }
                        if (style.overflowX !== 'visible') value = clip(value, { ...value, left: box.left, right: box.right });
                        if (value && style.overflowY !== 'visible') value = clip(value, { ...value, top: box.top, bottom: box.bottom });
                    }
                    if (value) rects.push(value);
                }
            }
        }
    }
    return rects;
}

const layoutScrolls = new WeakSet<EventTarget>();
export function isRowActionsLayoutScroll(target: EventTarget | null): boolean {
    return target !== null && layoutScrolls.has(target);
}

/** Reveal the temporary lane; its own bounded scroll must not dismiss its actions. */
export function revealRowActionsSpace(space: Element): void {
    const wrapper = space.closest('.table-wrapper');
    if (!wrapper) return;
    const target = space.getBoundingClientRect(), bounds = wrapper.getBoundingClientRect();
    const delta = target.bottom > bounds.bottom ? target.bottom - bounds.bottom : target.top < bounds.top ? target.top - bounds.top : 0;
    if (!delta) return;
    layoutScrolls.add(wrapper);
    wrapper.scrollTop += delta;
    requestAnimationFrame(() => requestAnimationFrame(() => layoutScrolls.delete(wrapper)));
}
